// CompUtils.ts

import {
  type AnalysisCompType,
  type Maybe,
  type Scalars,
} from "@/generated/graphql"
import {
  abbreviateCurrency,
  distance,
  formatShortDate,
  numberToCurrency,
  numberWithCommas,
  parseCoordinate,
} from "./NumberUtils"
import type { AnalysisWithCompsType } from "@/queries/analysis"

export function extractPrice(
  pricing?: Maybe<Scalars["JSONString"]["output"]>
): number | null {
  // Check if pricing is a string before attempting to parse it
  if (typeof pricing === "string") {
    try {
      const parsed = JSON.parse(pricing)
      return typeof parsed?.price === "number" ? parsed.price : null
    } catch (error) {
      console.error("Parsing error:", error)
    }
  } else {
    console.warn("Pricing is not a string:", pricing)
  }
  return null // Return null if pricing is not a string or in case of parsing errors
}

export const calculatedDistance = (
  lat1: number | null | undefined,
  lon1: number | null | undefined,
  lat2: number | null | undefined,
  lon2: number | null | undefined
): number | null => {
  if (
    typeof lat1 !== "number" ||
    typeof lon1 !== "number" ||
    typeof lat2 !== "number" ||
    typeof lon2 !== "number"
  ) {
    return null
  }
  return distance(lat1, lon1, lat2, lon2, "MILES")
}

export type CompCompareTextOption =
  | "suggested"
  | "distance"
  | "soldDate"
  | "pricePerSqft"
  | "beds"
  | "baths"
  | "yearBuilt"
  | "squareFeet"
  | "soldPrice"
  | "daysOnMarket"
  | "status"
  | "cityStateZip"
  | "updatedAt"

export function compCompareText(
  label: CompCompareTextOption,
  comp: AnalysisCompType,
  analysis: AnalysisWithCompsType | null
) {
  const { arvcomp } = comp

  if (!arvcomp) {
    return null
  }
  switch (label) {
    case "suggested":
      if (comp.originalIndex && comp.originalIndex < 4) {
        return "OUR PICK"
      }
      if (comp.isSelected) {
        return "YOUR PICK"
      }
      return "ADD"
    case "distance": {
      const calcDistance: number | null = calculatedDistance(
        parseCoordinate(analysis?.property.latitude) ?? 0,
        parseCoordinate(analysis?.property.longitude) ?? 0,
        parseCoordinate(arvcomp.latitude) ?? 0,
        parseCoordinate(arvcomp.longitude) ?? 0
      )
      return calcDistance ? `${calcDistance.toFixed(1).toString()} Mi` : ""
    }
    case "soldDate": {
      const isSaleOrSold: boolean = ["sale", "sold"].includes(
        arvcomp.status?.toLowerCase() ?? ""
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return isSaleOrSold && arvcomp.dateSold
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          formatShortDate(arvcomp.dateSold || "")
        : ""
    }
    case "updatedAt":
    case "pricePerSqft":
      return arvcomp.pricePerSqft
        ? `${numberToCurrency(Math.round(arvcomp.pricePerSqft || 0))}/Sf`
        : ""
    case "soldPrice":
      return abbreviateCurrency(
        arvcomp.soldPrice ||
          (JSON.parse(arvcomp.pricing || "{}") as { price?: number }).price
      )
    case "yearBuilt":
      return arvcomp.yearBuilt ? `${arvcomp.yearBuilt} Blt` : ""
    case "squareFeet":
      return `${numberWithCommas(arvcomp.area || 0)} Sf`
    case "baths":
      return `${arvcomp.bathrooms ?? "N/A"} Ba`
    case "beds":
      return `${arvcomp.bedrooms ?? "N/A"} Bd`
    case "daysOnMarket":
      return arvcomp.daysOnMarket
        ? `${arvcomp.daysOnMarket.toString()} DOM`
        : ""
    case "status":
      return arvcomp.status || ""
    case "cityStateZip":
      return `${arvcomp.city}, ${arvcomp.state} ${arvcomp.zipcode}`
    default:
      return null
  }
}

export const calculateZoomLevel = (
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number,
  mapWidth: number,
  mapHeight: number
): number => {
  // Calculate the latitude and longitude ranges
  const latRange = maxLat - minLat
  const lonRange = maxLon - minLon

  // Determine the zoom level based on the range of latitude and longitude
  let zoomLevel = 12 // Default zoom level
  if (latRange < 0.05 && lonRange < 0.05) {
    zoomLevel = 15 // Very close zoom
  } else if (latRange < 0.1 && lonRange < 0.1) {
    zoomLevel = 13 // Close zoom
  } else if (latRange < 0.2 && lonRange < 0.2) {
    zoomLevel = 12 // Medium zoom
  } else if (latRange < 0.5 && lonRange < 0.5) {
    zoomLevel = 11 // Distant zoom
  }

  // Adjust zoom based on the aspect ratio of the map
  const aspectRatio = mapWidth / mapHeight
  if (aspectRatio > 1) {
    zoomLevel -= 1 // Wide maps require a bit of a zoom out
  }

  // console.log(zoomLevel, latRange, lonRange)

  return zoomLevel
}
